import React from "react";

import Grid from "@material-ui/core/Grid";
import { useFormikContext } from "formik";
import { get } from "lodash";
import styled from "styled-components/macro";

import {
  CalculationBasis,
  CalculationBasisDisplayNameMap,
} from "constants/interest";

import { Input, PercentageInput, SelectField, withNamespace } from ".";
import { CheckBox } from "./CheckBox";
import { HelpText } from "./HelpText";
import { Label } from "./Label";

const CheckboxGrid = styled(Grid)`
  align-self: flex-end;
  text-align: start;
`;

const CALCULATION_BASIS_OPTIONS = Object.values(CalculationBasis).map(
  value => ({ value, label: CalculationBasisDisplayNameMap[value] }),
);

const AgencyCheckbox = () => {
  return (
    <CheckBox
      label="Enable Interest"
      name={withNamespace("billingSettings", "enableInterest")}
      dataTour="enableInterest"
    />
  );
};

const BusinessCheckbox = ({ ns }) => {
  return (
    <CheckBox
      name={withNamespace(ns, "overrideInterestSettings")}
      label="Override Interest settings from my Agency settings"
    />
  );
};

export function InterestSettingForm(props) {
  const {
    namespace = "",
    name = "interestSettings",
    label = null,
    tooltip,
    useBusinessCheckbox = false,
  } = props;

  const { values } = useFormikContext();
  const { overrideInterestSettings, billingSettings = {} } = values;

  const { enableInterest } = billingSettings;

  const isBusinessBalance =
    get(values, `${withNamespace(namespace, name)}.0.calculationBasis`) ===
    CalculationBasis.BUSINESS_BALANCE;

  const showInterestForm = useBusinessCheckbox
    ? overrideInterestSettings
    : enableInterest;

  return (
    <Grid data-tour="interest" container spacing={2} item xs={12}>
      <Grid item xs={12}>
        {label ? <Label tooltip={tooltip}>{label}</Label> : null}
      </Grid>
      <Grid item xs={12}>
        {useBusinessCheckbox ? (
          <BusinessCheckbox ns={namespace} />
        ) : (
          <AgencyCheckbox />
        )}
      </Grid>
      {showInterestForm && (
        <>
          <Grid item xs={6}>
            <PercentageInput
              name={`${withNamespace(namespace, name)}.0.debitInterestRate`}
              label="Debit Interest Rate"
              tooltip={
                isBusinessBalance
                  ? "Applies while the business owes money to the agency, based on the business's net account balance"
                  : "Applies to overdue invoices and charges interest on them"
              }
            />
          </Grid>
          <Grid item xs={6}>
            <PercentageInput
              name={`${withNamespace(namespace, name)}.0.creditInterestRate`}
              label="Credit Interest Rate"
              tooltip={
                isBusinessBalance
                  ? "Applies while the agency owes money to the business, based on the business's net account balance"
                  : "Applies to overdue RCTIs and charges interest on them"
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Input
              name={`${withNamespace(namespace, name)}.0.daysOverdueThreshold`}
              label="Days Overdue Threshold"
              type="number"
              afterSymbol="days"
              tooltip="Sets the grace period, in days, before an overdue transaction can be charged interest"
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              name={`${withNamespace(namespace, name)}.0.calculationBasis`}
              label="Calculation Basis"
              options={CALCULATION_BASIS_OPTIONS}
              tooltip="Choose whether interest is calculated per overdue document or on the business's net account balance"
            />
          </Grid>
          {!useBusinessCheckbox && (
            <CheckboxGrid item xs={6}>
              <CheckBox
                label="Compound?"
                name={withNamespace(
                  "billingSettings",
                  "enableCompoundInterest",
                )}
                tooltip="Include overdue interest charges with the original overdue amount when calculating interest"
              />
            </CheckboxGrid>
          )}
          <Grid item xs={12}>
            <HelpText>
              {isBusinessBalance
                ? "Interest accrues daily on the business's net account balance. Only documents past the grace period count toward the balance."
                : "Interest accrues daily from the due date once the grace period has passed."}
            </HelpText>
          </Grid>
        </>
      )}
    </Grid>
  );
}
